import { confluenceLocalStorageInstance as localStorage } from '@confluence/storage-manager/entry-points/storageInstance.ts';
import { keys } from '@confluence/storage-manager/entry-points/LocalStorageKeys.ts';

import { EDITOR_PARENT_CLASS, EDITOR_CHILDREN_SELECTOR } from './constants.ts';

type ElementObject = {
	elementObject: {
		index: number;
		contentId: string;
	};
};

export const scrollToLastViewedPosition = (contentId: string): void => {
	// grab the element object from local storage
	const { elementObject } = getScrollParams() as ElementObject;

	// cleanup local storage once we have the object
	cleanUpLocalStorageScrolls();

	// return if elementObject doesn't exist or have index and contentId properties
	if (!elementObject?.index || !elementObject?.contentId) return;

	// return if the index is -1 or stored contentId doesn't match current contentId
	if (elementObject?.index < 0 || elementObject?.contentId !== contentId) return;

	// grab the editor parent element
	const scrollParentElement = Array.from(
		// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
		document.getElementsByClassName(EDITOR_PARENT_CLASS) as HTMLCollectionOf<Element>,
	)[0] as HTMLElement;

	// return if editor parent doesn't exist
	if (!scrollParentElement) {
		return;
	}

	// set scroll behavior to auto, which will make it "jump" instead of scroll
	scrollParentElement.style.scrollBehavior = 'auto';

	// grab all of the direct children elements from the editor parent
	const elements = Array.from(scrollParentElement.querySelectorAll(EDITOR_CHILDREN_SELECTOR)) as [
		HTMLElement,
	];

	if (elements.length > 0) {
		const idealElement: HTMLElement | null = elements[elementObject.index];

		if (idealElement) {
			// minor offset for visual whitespace
			const addtlOffset: number = 10;

			scrollParentElement.scrollTo({
				top: idealElement.offsetTop - addtlOffset,
			});

			placeCursor(idealElement);
		}
	}

	// cleanup scrollBehavior
	scrollParentElement.style.scrollBehavior = '';
};

const placeCursor = (idealElement: HTMLElement) => {
	// we need to use range to place the cursor inside a contenteditable element
	// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
	if (document.createRange) {
		// check to see if the direct child of the selected element is a text node, and return if not
		if (!(idealElement?.childNodes[0]?.nodeType === Node.TEXT_NODE)) {
			return;
		}
		// sets the range to contain the contents of the idealElement node, collapse indicating it should be at the end of the text node
		// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
		const range = document.createRange();
		range.selectNodeContents(idealElement);
		range.collapse(false);

		// clear existing ranges and add the newly created range
		const selection = window.getSelection();
		if (selection) {
			selection.removeAllRanges();
			selection.addRange(range);
		}
	}
};

const getScrollParams = (): ElementObject => {
	return {
		elementObject: localStorage.getItem(
			keys.LOCAL_STORAGE_CONTENT_VIEW_CLOSEST_ELEMENT_SCROLL_POSITION,
		),
	};
};

const cleanUpLocalStorageScrolls = (): void => {
	return localStorage.removeItem(keys.LOCAL_STORAGE_CONTENT_VIEW_CLOSEST_ELEMENT_SCROLL_POSITION);
};
